import {
  applyPromoCode,
  removePromoCode,
  validatePromoCode,
} from "@/api/rest/promotionCodeApi";
import { useAppSelector } from "@/redux/hooks";
import { useTrackActions } from "@/utils/hooks/useTracking";
import { TABLET_SCREEN_SIZE, isShopifyProduct } from "@brandclub/common-ui";
import { Box, Button, CircularProgress, Skeleton, styled } from "@mui/material";
import { useState, useCallback, useEffect } from "react";
import { useSplitTreatments } from "@splitsoftware/splitio-react";
import { useCheckoutSimplifiedUI } from "../../CheckoutSimplifiedUIProvider";
import { CartTotal } from "./CartTotal";
import { OrderSummaryProduct } from "./OrderSummaryProduct";
import { PromotionCode, AppliedPromoCode } from "./PromotionCode";

const Container = styled("div")({
  boxSizing: "border-box",
  display: "flex",
  borderRadius: "8px",
  flexDirection: "column",
  gap: "20px",
  width: "100%",
  backgroundColor: "#f6f8fa",
  padding: "24px",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    gap: "16px",
    backgroundColor: "white",
    padding: "16px",
    maxWidth: "100%",
  },
});

const CartContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  width: "100%",
});

const SectionTitle = styled("div")(({ theme }) => ({
  fontSize: "20px",
  fontWeight: 600,
  color: theme.vars.palette.text.primary,
  marginBottom: "4px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
}));

const PlaceOrderButton = styled(Button)({
  width: "100%",
  padding: "14px 24px",
  fontSize: "16px",
  fontWeight: 600,
  textTransform: "none",
  marginTop: "16px",
});

interface OrderSummaryProps {
  showOrderSummaryTitle?: boolean;
}

const OrderSummary = ({ showOrderSummaryTitle = true }: OrderSummaryProps) => {
  const {
    session,
    sessionLoading,
    productMetadata,
    triggerPaymentSubmission,
    updateSession,
  } = useCheckoutSimplifiedUI();
  const [trackAction] = useTrackActions();
  const [appliedPromoCode, setAppliedPromoCode] =
    useState<AppliedPromoCode | null>(null);
  const [couponValidationError, setCouponValidationError] = useState<
    string | null
  >(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

  // Check Split.io treatment for coupon feature
  const domainName = useAppSelector(
    (state) => state.appConfig?.domainConfig.domainName
  );
  const userProfile = useAppSelector(({ userProfile }) => userProfile);
  const { treatments } = useSplitTreatments({
    names: ["brandclub_dtc_checkout_simplified"],
    attributes: {
      email: userProfile?.email ?? "",
      appId: domainName ?? "",
    },
  });
  const isCouponFeatureEnabled =
    treatments?.brandclub_dtc_checkout_simplified?.treatment ===
    "on_with_coupon";

  // Initialize appliedPromoCode from session.applied_coupon on mount/session change
  useEffect(() => {
    if (session?.applied_coupon) {
      setAppliedPromoCode({
        code: session.applied_coupon.code,
        description: session.applied_coupon.description,
        discountAmount: session.applied_coupon.calculated_discount || 0,
      });
    } else {
      setAppliedPromoCode(null);
    }
  }, [session?.applied_coupon]);

  // Handle apply promo code
  const handleApplyPromoCode = useCallback(
    async (code: string, orderId: string): Promise<boolean> => {
      const response = await applyPromoCode({ code, orderId });
      if (response.success) {
        setAppliedPromoCode({
          code: response.code,
          description: response.description,
          discountAmount: response.discountAmount,
        });
        // Update session to refresh totals (including discount)
        if (response.session) {
          updateSession(response.session);
        }
        return true;
      }
      return false;
    },
    [updateSession]
  );

  // Handle remove promo code
  const handleRemovePromoCode = useCallback(
    async (code: string, orderId: string) => {
      const response = await removePromoCode({ code, orderId });
      setAppliedPromoCode(null);
      // Update session to refresh totals (remove discount)
      if (response.session) {
        updateSession(response.session);
      }
    },
    [updateSession]
  );

  if (sessionLoading) {
    return (
      <Container>
        <Skeleton variant="rectangular" height={25} width="60%" />
        <Skeleton variant="rectangular" height={80} />
        <Skeleton variant="rectangular" height={80} />
        <Skeleton variant="rectangular" height={120} />
      </Container>
    );
  }

  if (!session?.line_items?.length) {
    return null;
  }

  // Extract totals from session
  const getTotalByType = (type: string): number => {
    const total = session.totals.find((t) => t.type === type);
    return total ? total.amount : 0; // Convert from minor units to dollars
  };

  const itemsBaseAmount = getTotalByType("items_base_amount");
  const itemsDiscount = getTotalByType("items_discount");
  const subtotal = itemsBaseAmount; // Show base amount as subtotal line
  const discount = itemsDiscount;
  const shipping = getTotalByType("fulfillment");
  const estimatedTax = getTotalByType("tax");
  const total = getTotalByType("total");

  // Get delivery estimate from selected fulfillment option
  const selectedOption = session.fulfillment_options.find(
    (opt) => opt.id === session.fulfillment_option_id
  );
  const deliveryEstimate =
    selectedOption?.type === "shipping" && selectedOption.subtitle
      ? selectedOption.subtitle
      : "";

  // Map line items to product display format with real metadata
  const products = session.line_items.map((item) => {
    const metadata = productMetadata[item.item.id];
    return {
      stacklineSku: item.item.id,
      retailerId: metadata?.retailerId,
      retailerSku: metadata?.retailerSku,
      retailerParentSku: metadata?.retailerParentSku,
      title: metadata?.title || `Product ${item.item.id}`,
      brandId: metadata?.brandId,
      brandName: metadata?.brandName,
      categoryId: metadata?.categoryId,
      categoryName: metadata?.categoryName,
      subCategoryId: metadata?.subCategoryId,
      subCategoryName: metadata?.subCategoryName,
      quantity: item.item.quantity,
      retailPrice: metadata?.retailPrice || item.base_amount,
      pixels: metadata?.pixels,
      image: metadata?.image || "",
    };
  });

  // Only show coupon input for Shopify/DTC checkout retailers AND when feature is enabled
  const retailerIdForCheckout = products.find(
    (p) => !!p.retailerId
  )?.retailerId;
  const shouldShowCouponInput =
    isCouponFeatureEnabled &&
    typeof retailerIdForCheckout === "number" &&
    isShopifyProduct(retailerIdForCheckout);

  const handlePlaceOrderClick = async () => {
    // Clear any previous validation errors
    setCouponValidationError(null);

    // If a coupon is applied, validate it first before submitting
    if (appliedPromoCode && session?.id) {
      setIsValidatingCoupon(true);
      try {
        const validationResult = await validatePromoCode(
          appliedPromoCode.code,
          session.id
        );

        if (!validationResult.valid) {
          // Coupon is no longer valid - show error and don't submit
          setCouponValidationError(
            validationResult.errorMessage ||
              "This coupon is no longer valid. Please remove it to continue."
          );
          setIsValidatingCoupon(false);
          return; // Stop - don't proceed with order
        }
      } catch (error) {
        console.error("Coupon validation error:", error);
        setCouponValidationError(
          "Failed to validate coupon. Please try again."
        );
        setIsValidatingCoupon(false);
        return; // Stop - don't proceed with order
      }
      setIsValidatingCoupon(false);
    }

    // Don't track CHECKOUT_ORDER_SUBMIT - only track success/failure
    // This avoids firing "Order Submitted" pixel events prematurely
    triggerPaymentSubmission?.();
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Container>
        {showOrderSummaryTitle && (
          <SectionTitle>
            <span>Order summary</span>
          </SectionTitle>
        )}

        <CartContainer>
          {products.map((item) => (
            <OrderSummaryProduct key={item.stacklineSku} product={item} />
          ))}
        </CartContainer>

        <Box
          sx={{
            width: "100%",
            marginTop: "8px",
          }}
        >
          {shouldShowCouponInput ? (
            <PromotionCode
              orderId={session.id}
              appliedCode={appliedPromoCode}
              onApplyCode={handleApplyPromoCode}
              onRemoveCode={handleRemovePromoCode}
              externalError={couponValidationError}
              onClearExternalError={() => setCouponValidationError(null)}
            />
          ) : null}
          <CartTotal
            subtotal={subtotal}
            discount={discount}
            shipping={shipping}
            estimatedTax={estimatedTax}
            total={total}
            itemCount={products.length}
            deliveryEstimate={deliveryEstimate}
            hasShippingAddress={!!session.fulfillment_address}
            hasShippingMethod={!!session.fulfillment_option_id}
          />
        </Box>
      </Container>

      <PlaceOrderButton
        variant="contained"
        color="primary"
        disabled={
          !triggerPaymentSubmission ||
          session?.status === "in_progress" ||
          isValidatingCoupon
        }
        onClick={handlePlaceOrderClick}
      >
        {isValidatingCoupon ? (
          <>
            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            Validating...
          </>
        ) : session?.status === "in_progress" ? (
          <>
            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            Processing...
          </>
        ) : (
          "Place Order"
        )}
      </PlaceOrderButton>
    </Box>
  );
};

export default OrderSummary;
